/* eslint-disable ember/no-classic-components, ember/no-computed-properties-in-native-classes, ember/require-tagless-components, no-restricted-properties, no-unused-vars, warp-drive/no-legacy-request-patterns */
import Component from '@ember/component';
import { tracked } from '@glimmer/tracking';
// Ember imports
import { alias } from '@ember/object/computed';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
// External imports
import SnapshotPdfType from '@linc-technologies/linced-common/enums/snapshot-pdf-type';
// Constants for zip-files partitioning
const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50 MB per 1 zip file
const MAX_FILE_COUNT = 400; // 400 reports per 1 zip file

export default class DownloadZip extends Component {
  SnapshotPdfType = SnapshotPdfType;

  // Services
  @service
  media;

  @service
  session;

  @service
  store;

  @service
  toaster;

  // Attributes
  @alias('session.activeSchool')
  schoolId;

  @tracked
  isGeneratingZip = false;
  @tracked
  processMonitorIds = [];
  @tracked
  filenames = [];
  snapshotSet = null;
  group = null;
  school = null;

  // createZipParts takes in a snapshotSet, iterates over each snapshot's pdfs array in the set
  // and returns an array of media ids split into zip parts based on the max file size and count constants
  async createZipParts(snapshotSet) {
    let mediaParts = [];
    //let snapshots = await snapshotSet.get('snapshotIds');
    let snapshots = await this.store.query('snapshots/snapshot', {
      schoolId: snapshotSet.school.get('id'),
      setId: snapshotSet.id,
      idsOnly: true,
      pdfsOnly: true,
    });
    // Create a map of all snapshots' pdfs and resolve any ember relationship proxy promises
    // so we can access attributes for comparisons here
    let snapshotsPdfs = await Promise.all(snapshots.map((s) => s.get('pdfs')));
    // Sanitize the array and rid of any empty pdf arrays
    let filteredSnapshotsPdfs = snapshotsPdfs.filter(
      (pdfsArray) => !isEmpty(pdfsArray.lastObject),
    );
    mediaParts = this.createPartsWithLimits(filteredSnapshotsPdfs);
    return mediaParts;
  }

  createPartsWithLimits(pdfsArray) {
    let mediaParts = [];
    let currentPart = [];
    let currentSize = 0;

    for (let i = 0; i < pdfsArray.length; i++) {
      const pdf = pdfsArray[i].lastObject;
      const fileSize = pdf.fileSize;
      const mediaId = pdf.mediaId;

      const exceedsSize = currentSize + fileSize > MAX_FILE_SIZE;
      const exceedsCount = currentPart.length >= MAX_FILE_COUNT;

      if (exceedsSize || exceedsCount) {
        if (currentPart.length > 0) {
          mediaParts.push(currentPart);
        }
        currentPart = [];
        currentSize = 0;
      }

      currentPart.push(mediaId);
      currentSize += fileSize;
    }

    // Push the last part if it has any items
    if (currentPart.length > 0) {
      mediaParts.push(currentPart);
    }

    return mediaParts;
  }

  @action
  async createZip() {
    this.isGeneratingZip = true;

    try {
      const mediaParts = await this.createZipParts(this.snapshotSet);

      const entityLabel = isEmpty(this.group)
        ? this.school.name
        : this.group.label;
      const base = `Bulk_Reports_for_${entityLabel}_${this.snapshotSet.startDate}_to_${this.snapshotSet.endDate}`;

      // build filenames in order, only suffix when multiple parts
      const multiple = mediaParts.length > 1;
      const filenames = mediaParts.map((_, i) =>
        multiple ? `${base}_Part_${i + 1}.zip` : `${base}.zip`,
      );

      // update filenames list at once (keeps template observers happy)
      this.filenames = filenames;

      // fire all zip creations concurrently; order preserved by input order
      const processMonitorIds = await Promise.all(
        mediaParts.map((part) => this.media.createZip(part, this.schoolId)),
      );

      this.processMonitorIds = processMonitorIds;
    } catch (e) {
      this.toaster.error(
        `Error downloading the zip. Refresh the page and try again.`,
      );
    }
  }

  @action
  async onZipComplete(monitors) {
    try {
      for (let i = 0; i < monitors.length; i++) {
        let monitor = monitors[i];
        let filename = this.filenames[i];

        if (monitor.resourceIds.length !== 1) {
          this.toaster.error(
            `Error downloading the zip: Incorrect number of zip storage keys returned. Refresh the page and try again`,
          );
          return;
        }

        // Download the file to a blob in local storage (authenticated)
        let storageKey = monitor.resourceIds.firstObject;
        let uri = await this.media.getZipBlobUri(
          storageKey,
          this.schoolId,
          filename,
        );

        // Give the browser the blob uri and tell it to 'download' the local blob
        let link = document.createElement('a');
        link.href = uri;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (e) {
      this.toaster.error(`Failed to start the download:${e}}`);
    } finally {
      this.isGeneratingZip = false;
    }
  }
}
